import { z } from "zod";
import { TripSummary } from "./catalog.ts";

/**
 * Checkout, payment, and booking contracts (G2.7). A guest opens a checkout
 * from a quote id, pays through the provider with the client secret the
 * response carries, and reads the checkout's state with their own capability
 * secret. Nothing a client sends marks anything paid: only a verified provider
 * event does. Money is integer US cents. The primitives below mirror the
 * private helpers in index.ts, which this module cannot import without a cycle.
 */

const Uuid = z.uuid();
const Instant = z.iso.datetime({ offset: true });
const Cents = z.number().int().describe("Integer US cents");
const EmailAddress = z
  .email()
  .max(254)
  .regex(/^[\x21-\x7e]+$/, "Addresses must be ASCII");
/**
 * Single-line text a guest types. C0 and C1 control characters are refused
 * here, as PostgreSQL's [[:cntrl:]] check refuses them, so bad input answers
 * 400 rather than failing in the database.
 */
const SingleLineText = (max: number) =>
  z
    .string()
    .trim()
    .min(1)
    .max(max)
    // biome-ignore lint/suspicious/noControlCharactersInRegex: the pattern exists to reject them.
    .regex(/^[^\u0000-\u001f\u007f-\u009f]*$/, "Control characters are not allowed");

export const CheckoutSecret = z
  .string()
  .regex(/^[A-Za-z0-9_-]{43}$/)
  .describe(
    "The guest's capability for this checkout: 32 random bytes, base64url without padding, generated by the guest's client and sent once at creation. TideGrid stores only its SHA-256. Send it as `Authorization: Bearer <secret>` to read or cancel the checkout. Never put it in a URL.",
  );

export const PaymentProviderName = z
  .enum(["fake", "stripe"])
  .describe("fake is the demo provider; stripe replaces it before any live booking");
export type PaymentProviderName = z.infer<typeof PaymentProviderName>;

export const BookerDetails = z
  .object({
    name: SingleLineText(120),
    email: EmailAddress,
  })
  .describe(
    "The person booking. Personal data: stored once for the booking and its messages, never in URLs, events, or logs",
  );
export type BookerDetails = z.infer<typeof BookerDetails>;

export const CheckoutSessionCreateRequest = z.object({
  quoteId: Uuid.describe("A quote from POST /v1/public/quotes, still within its validity"),
  acceptedPolicyVersion: z
    .number()
    .int()
    .min(1)
    .describe("The policy version the guest was shown and accepted; must be the quote's"),
  booker: BookerDetails,
  checkoutSecret: CheckoutSecret,
});
export type CheckoutSessionCreateRequest = z.infer<typeof CheckoutSessionCreateRequest>;

export const CheckoutSessionState = z
  .enum(["open", "confirmed", "unfulfilled", "failed", "expired", "canceled"])
  .describe(
    "open: awaiting payment. confirmed: paid and booked. unfulfilled: the payment arrived but the trip could not be confirmed; it is refunded in full (see refund). failed: the payment failed. expired: time ran out before payment. canceled: the guest abandoned it",
  );
export type CheckoutSessionState = z.infer<typeof CheckoutSessionState>;

export const RefundState = z
  .enum(["requested", "succeeded", "failed"])
  .describe("requested until the provider confirms it; a failed refund is an operator exception");

export const CheckoutSession = z
  .object({
    id: Uuid,
    state: CheckoutSessionState,
    quoteId: Uuid,
    tripId: Uuid,
    partySize: z.number().int().min(1),
    amount: Cents.describe("What the payment charges: the quote's total"),
    currency: z.literal("USD"),
    expiresAt: Instant.describe(
      "Pay before this instant. The seats are held until then; a later payment is booked only if they are still free, and refunded otherwise",
    ),
    createdAt: Instant,
    booking: z
      .object({
        reference: z
          .string()
          .regex(/^[0-9A-HJKMNP-TV-Z]{8}$/)
          .describe("What the guest and the operator quote to each other"),
      })
      .nullable()
      .describe("Present once confirmed"),
    refund: z
      .object({ state: RefundState, amount: Cents })
      .nullable()
      .describe("Present when a payment could not be honored"),
  })
  .describe("A guest's checkout, as the guest may see it: no personal or provider detail");
export type CheckoutSession = z.infer<typeof CheckoutSession>;

export const CheckoutPayment = z
  .object({
    provider: PaymentProviderName,
    paymentRef: z.string().describe("The provider's payment id"),
    clientSecret: z
      .string()
      .describe(
        "What the guest's browser passes to the provider to pay. Not stored by TideGrid; keep it out of URLs and logs",
      ),
  })
  .describe("What the guest needs to pay at the provider");
export type CheckoutPayment = z.infer<typeof CheckoutPayment>;

export const CheckoutSessionCreateResponse = z.object({
  checkoutSession: CheckoutSession.describe(
    "The checkout as it was opened. A replay returns this same snapshot; read the current state with GET",
  ),
  payment: CheckoutPayment.nullable().describe(
    "Null when the checkout is no longer open or has passed its expiry",
  ),
});
export type CheckoutSessionCreateResponse = z.infer<typeof CheckoutSessionCreateResponse>;

export const CheckoutSessionResponse = z.object({ checkoutSession: CheckoutSession });
export type CheckoutSessionResponse = z.infer<typeof CheckoutSessionResponse>;

export const CheckoutSessionParams = z.object({
  checkoutSessionId: z.string().describe("Checkout session UUID; anything else answers 404"),
});

// Provider webhooks -----------------------------------------------------------------

export const PaymentWebhookParams = z.object({
  provider: z.string().describe("The configured payment provider; anything else answers 404"),
});

export const PaymentWebhookResponse = z
  .object({
    received: z.literal(true),
    duplicate: z.boolean().describe("The event id was already in the inbox"),
    outcome: z.string().describe("What processing did, for the provider's delivery log"),
  })
  .describe("The event was verified and recorded");
export type PaymentWebhookResponse = z.infer<typeof PaymentWebhookResponse>;

// Fake provider demo controls ---------------------------------------------------------

export const FakePaymentParams = z.object({
  paymentRef: z.string().describe("The fake payment id; anything else answers 404"),
});

export const FakePaymentEventParams = z.object({
  paymentRef: z.string(),
  eventId: z.string().describe("A stored event of this payment; anything else answers 404"),
});

export const FakePaymentSettleRequest = z.object({
  deliver: z
    .boolean()
    .default(true)
    .describe(
      "Deliver the signed event now. False records it for a later, late, or duplicate redelivery",
    ),
});
export type FakePaymentSettleRequest = z.infer<typeof FakePaymentSettleRequest>;

export const FakePaymentEvent = z.object({
  id: z.string(),
  type: z.enum(["payment.succeeded", "payment.failed"]),
  createdAt: Instant,
});

export const FakePayment = z.object({
  paymentRef: z.string(),
  status: z.enum(["pending", "succeeded", "failed"]),
  amount: Cents,
  currency: z.literal("USD"),
  amountRefunded: Cents,
  createdAt: Instant,
  events: z.array(FakePaymentEvent),
});

export const FakePaymentResponse = z.object({ payment: FakePayment });
export type FakePaymentResponse = z.infer<typeof FakePaymentResponse>;

export const FakeDelivery = z
  .object({
    status: z.number().int().describe("The webhook route's HTTP status"),
    outcome: z.string().nullable().describe("What processing did, or null when it failed"),
    duplicate: z.boolean().nullable(),
  })
  .describe("One delivery of a signed event to the real webhook route");

export const FakePaymentControlResponse = z.object({
  event: FakePaymentEvent,
  alreadySettled: z
    .boolean()
    .describe(
      "The payment had settled before this call, so the event is its existing outcome, not a new one. A redelivery settles nothing, and a payment has an event only once it has settled, so a redelivery always answers true",
    ),
  delivery: FakeDelivery.nullable().describe("Null when delivery was held back"),
});
export type FakePaymentControlResponse = z.infer<typeof FakePaymentControlResponse>;

// Booking source (G2.12d) -------------------------------------------------------------

export const BookingSource = z
  .enum(["direct", "staff_assisted"])
  .describe(
    "direct: booked by the guest online. staff_assisted: booked by an owner or booking staff member for a guest who phoned, emailed, or walked up, and paid through the same verified payment",
  );
export type BookingSource = z.infer<typeof BookingSource>;

export const BookingChannel = z
  .enum(["phone", "email", "walk_up"])
  .describe("How the guest reached staff, on a staff-assisted booking");
export type BookingChannel = z.infer<typeof BookingChannel>;

// Staff reads ------------------------------------------------------------------------

export const StaffBooking = z.object({
  id: Uuid,
  reference: z.string(),
  tripId: Uuid,
  partySize: z.number().int().min(1),
  state: z
    .enum(["confirmed", "canceled"])
    .describe("canceled: the guest canceled within policy (lane 1, G2.11c)"),
  source: BookingSource,
  reacquired: z
    .boolean()
    .describe("Confirmed after its hold had expired, by taking the seats again"),
  confirmedAt: Instant,
  booker: z.object({ name: z.string(), email: z.string() }),
  order: z.object({
    id: Uuid,
    status: z.enum(["pending", "paid", "void"]),
    total: Cents,
    currency: z.literal("USD"),
  }),
  payment: z.object({
    id: Uuid,
    provider: PaymentProviderName,
    state: z.enum(["pending", "succeeded", "failed"]),
    amount: Cents,
  }),
});
export type StaffBooking = z.infer<typeof StaffBooking>;

export const TripBookingsResponse = z
  .object({ bookings: z.array(StaffBooking) })
  .describe("Every booking on the trip, oldest first");
export type TripBookingsResponse = z.infer<typeof TripBookingsResponse>;

export const FinalizationReason = z
  .enum([
    "no_capacity",
    "trip_canceled",
    "trip_unavailable",
    "sales_closed",
    "party_size_out_of_range",
    "session_failed",
    "session_canceled",
    "payment_mismatch",
  ])
  .describe(
    "Why a verified payment could not become a booking. payment_mismatch is not refunded automatically; investigate it at the provider",
  );

export const FinalizationException = z.object({
  id: Uuid,
  reason: FinalizationReason,
  checkoutSessionId: Uuid,
  tripId: Uuid,
  paymentId: Uuid,
  amount: Cents.describe("What the checkout charged: its order's total"),
  createdAt: Instant.describe("When the payment was found unable to become a booking"),
  refund: z
    .object({
      id: Uuid,
      state: RefundState,
      amount: Cents,
      failureCode: z.string().nullable(),
      settledAt: Instant.nullable(),
    })
    .nullable(),
  // G2.12b: what an operator needs to follow it up.
  trip: TripSummary.describe("The trip the checkout was for"),
  partySize: z.number().int().min(1),
  checkout: z.object({
    state: CheckoutSessionState,
    expiresAt: Instant.describe("When the checkout's hold ran out, or would have"),
  }),
  payment: z.object({
    provider: PaymentProviderName,
    providerReference: z
      .string()
      .nullable()
      .describe("The provider's payment id, masked: its prefix and last four characters"),
    receivedAt: Instant.describe("When TideGrid verified the provider's event"),
    reportedAmount: Cents.nullable().describe(
      "The amount the provider's event reported; differs from amount on a payment_mismatch",
    ),
    reportedCurrency: z.string().nullable(),
  }),
  booker: z
    .object({ name: z.string(), email: z.string() })
    .optional()
    .describe(
      "Personal data, for following up with the guest. Present only for roles that hold bookings.read (owners and booking staff); absent otherwise",
    ),
});
export type FinalizationException = z.infer<typeof FinalizationException>;

export const FinalizationExceptionQuery = z.object({
  limit: z.coerce.number().int().min(1).max(100).default(50),
  before: Uuid.optional().describe(
    "Cursor: the last exception id of the previous page (nextBefore). One that names no exception of this operator answers 400 cursor_invalid",
  ),
});

export const FinalizationExceptionsResponse = z
  .object({
    exceptions: z.array(FinalizationException),
    nextBefore: Uuid.nullable().describe("Pass as before for the next page; null on the last page"),
  })
  .describe("Payments that could not become bookings, newest first, for operator follow-up");
export type FinalizationExceptionsResponse = z.infer<typeof FinalizationExceptionsResponse>;
